import { formatAttorneyFee } from "@/lib/utils/attorneyFeeUtils";
import { AttorneyFeeSelect } from "@/components/features/matter/AttorneyFeeSelect/AttorneyFeeSelect";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
  SimpleSelectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  useCreateMatterStatus,
  useDeleteMatterStatus,
  useUpdateMatterStatus,
} from "@/lib/data/react-query/matters/matter-status";
import { MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterStatus, type MatterStatusCategory } from "@/lib/types";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { toLower } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const MATTER_STATUS_FORM_ID = "matter-status-editor-form";

const formSchema = z.object({
  categoryId: z.string().min(1, { message: "Category is required" }),
  defaultAttorneyFee: z.string().optional(),
  description: z.string().nullable().optional(),
  goal: z.preprocess(
    (value) => (value === "" ? null : value),
    z.coerce.number().nullable().optional(),
  ),
  label: z.string().min(1, { message: "Label is required" }),
});

const defaultFormValues = {
  label: "",
  description: "",
  categoryId: "pre-litigation" as const,
  goal: null,
  defaultAttorneyFee: "33.33",
};

export interface AddOrEditMatterStatusBaseProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export type ConditionalProps =
  | {
      isReadOnly?: boolean;
      editing: true;
      activeMatterStatus: Pick<MatterStatus, "id" | "dateModified"> &
        Partial<MatterStatus>;
      setActiveMatterStatusId: (id: string) => void;
    }
  | {
      isReadOnly?: never;
      editing: false;
      activeMatterStatus?: never;
      setActiveMatterStatusId?: never;
    };

type AddOrEditMatterStatusModalProps = AddOrEditMatterStatusBaseProps &
  ConditionalProps & {
    categoryOptions: readonly MatterStatusCategory[];
    defaultCategoryId?: string;
  };

export function AddOrEditMatterStatusModal({
  open,
  setOpen,
  activeMatterStatus,
  editing,
  isReadOnly,
  categoryOptions,
  defaultCategoryId,
}: AddOrEditMatterStatusModalProps) {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const [options, setOptions] = useState(MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editing
      ? {
          ...defaultFormValues,
          ...activeMatterStatus,
          defaultAttorneyFee: activeMatterStatus.defaultAttorneyFee
            ? String(activeMatterStatus.defaultAttorneyFee)
            : defaultFormValues.defaultAttorneyFee,
          categoryId:
            activeMatterStatus.categoryId ?? defaultFormValues.categoryId,
        }
      : {
          ...defaultFormValues,
          categoryId:
            defaultCategoryId ??
            categoryOptions.at(0)?.id ??
            defaultFormValues.categoryId,
        },
    mode: "onSubmit",
  });

  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );

  const matterStatuses = useMemo(
    () => matterSettingsData?.data.statuses ?? [],
    [matterSettingsData],
  );
  const createMatterStatus = useCreateMatterStatus();
  const editMatterStatus = useUpdateMatterStatus();
  const deleteMatterStatus = useDeleteMatterStatus();
  const isFormLoading =
    createMatterStatus.isPending ||
    editMatterStatus.isPending ||
    deleteMatterStatus.isPending;

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (
      matterStatuses.some(
        (matterStatus) =>
          toLower(matterStatus.label) === toLower(data.label) &&
          matterStatus.id !== activeMatterStatus?.id,
      )
    ) {
      form.setError("label", {
        message: "Matter status label must be unique",
      });
      return;
    }
    if (editing) {
      await editMatterStatus.mutateAsync({
        ...data,
        id: activeMatterStatus.id,
        defaultAttorneyFee: Number(data.defaultAttorneyFee),
        dateModified: activeMatterStatus.dateModified,
      });
    } else {
      await createMatterStatus.mutateAsync({
        ...data,
        defaultAttorneyFee: Number(data.defaultAttorneyFee),
      });
    }
    setOpen(false);
    form.reset();
  };

  const handleDelete = async () => {
    setOpen(false);
    if (editing) {
      await deleteMatterStatus
        .mutateAsync({
          id: activeMatterStatus.id,
        })
        .then(() => {
          setIsDeleting(false);
        });
    }
  };

  const sheetTitle = useMemo(
    () =>
      isReadOnly
        ? "View matter status"
        : editing
          ? "Update matter status"
          : "Create matter status",
    [isReadOnly, editing],
  );

  const sheetDescription = useMemo(
    () =>
      isReadOnly
        ? "View the matter status details below"
        : editing
          ? "Update the matter status details below"
          : "Create a new matter status to your firm",
    [isReadOnly, editing],
  );

  useEffect(() => {
    if (!open) {
      form.reset();
      setOptions(MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS);
    }
  }, [open, form]);

  useEffect(() => {
    const currentValue = String(form.watch("defaultAttorneyFee"));
    if (!currentValue) {
      setOptions(MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS);
      return;
    }
    const currentValueInDefaults = MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS.find(
      (option) => option.value === currentValue,
    );
    if (currentValueInDefaults) {
      setOptions(MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS);
      return;
    }
    setOptions([
      ...MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS,
      {
        value: String(currentValue),
        label: formatAttorneyFee(currentValue),
      },
    ]);
  }, [form, open]);

  const showDeleteInFooter =
    !isReadOnly &&
    editing &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterStatus,
      ACTION_PERMISSION_VALUES.delete,
    );

  const deleteButton = showDeleteInFooter && (
    <div>
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    </div>
  );

  const cancelAndSubmitFooter = (
    <div className="flex grow justify-end space-x-2">
      <FormCancelButton
        label={isReadOnly ? "Close" : "Cancel"}
        setOpen={setOpen}
        isDirty={form.formState.isDirty}
      />
      {!isReadOnly && (
        <FormSubmitButton
          submitFormId={MATTER_STATUS_FORM_ID}
          loading={isFormLoading}
          disabled={isFormLoading}
        />
      )}
    </div>
  );

  const fieldsetInner = (
    <fieldset disabled={isReadOnly} className={slideoverFieldsetClassName}>
      <TextFormField register={form.register("label")} label="Label*" />
      <SimpleSelectFormField
        items={categoryOptions.map((category) => category.id)}
        formatItemLabel={(categoryId) =>
          categoryOptions.find((category) => category.id === categoryId)
            ?.label ?? categoryId
        }
        label="Category"
        register={form.register("categoryId")}
      />
      <div className="col-span-2">
        <TextAreaFormField
          register={form.register("description")}
          label="Description"
        />
      </div>
      <TextFormField
        register={form.register("goal", {
          valueAsNumber: true,
        })}
        label="Days in status goal"
        type="number"
      />
      <FormField
        control={form.control}
        name="defaultAttorneyFee"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>Attorney fee* (%)</FormLabel>
              <FormMessage />
            </div>
            <AttorneyFeeSelect
              placeholder={`Select or enter an attorney fee`}
              value={field.value?.toString() ?? ""}
              onChange={(value) => {
                form.setValue("defaultAttorneyFee", value, {
                  shouldDirty: true,
                });
              }}
              options={options}
              createMessage={`Add an attorney fee`}
              emptyMessage={`No attorney fee found`}
              label={``}
            />
          </FormItem>
        )}
      />
    </fieldset>
  );

  const sharedModals = (
    <ActionConfirmationModal
      title={`Are you sure you want to delete this matter status?`}
      open={isDeleting && !!activeMatterStatus}
      setOpen={setIsDeleting}
      afterActionFn={async () => {
        if (activeMatterStatus) {
          await handleDelete();
        }
      }}
    />
  );

  return (
    <>
      {/* Form must wrap AppModal so footer controls (FormSubmitButton useFormContext) are inside the provider */}
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={sheetTitle}
          description={sheetDescription}
          size="2xl"
          loading={isFormLoading}
          isDirty={form.formState.isDirty}
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              {deleteButton}
              {cancelAndSubmitFooter}
            </div>
          }
        >
          <form
            id={MATTER_STATUS_FORM_ID}
            className="relative"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {fieldsetInner}
          </form>
        </AppModal>
      </Form>
      {sharedModals}
    </>
  );
}
